import { Link, useNavigate } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import { CircleArrowLeft } from 'lucide-react';
import { parse, unparse } from 'papaparse';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  isParentsBulkUploadQueuedResponse,
  useBulkUploadParentsMutation,
} from '~entities/parent/api/upload-parent-csv';
import type { ParentsBulkImportJobResult } from '~entities/parent/api/get-parents-bulk-import-status';
import { useGetParentsBulkImportStatus } from '~entities/parent/api/get-parents-bulk-import-status';

import { type ParentRegistrationData } from '~pages/parents/bulk-upload/validation';

import { Button } from '~shared/shadcn/ui/button';

import {
  clearParentsBulkUploadDraft,
  fileFromDraft,
  readParentsBulkUploadDraft,
  writeParentsBulkUploadDraft,
  type BulkUploadStep,
} from './bulk-upload-draft';
import { ColumnMapping } from './column-mapping';
import { countParentGroupsFromCsvRows } from './count-parent-groups';
import { ParentsBulkImportProgressModal } from './parents-bulk-import-progress-modal';
import { TABLE_DATA } from './constants';
import { FileSelector } from './file-selector';
import type {
  ParentRegistrationColumnMappingTableData,
  ParentRegistrationCSVHeader,
} from './types';
import { ValidateCSV } from './validate-csv';

function getMaxStudentFromHeaders(headers: Array<string>): number {
  let max = 0;
  const re = /^Student (\d+) /;
  for (const h of headers) {
    const m = re.exec(h);
    if (m?.[1]) max = Math.max(max, parseInt(m[1], 10));
  }
  return max;
}

function filterRowsByStudentCount<T extends { header: string }>(
  rows: Array<T>,
  maxStudent: number,
): Array<T> {
  const re = /^Student (\d+) /;
  return rows.filter((row) => {
    const m = re.exec(row.header);
    if (!m?.[1]) return true;
    return parseInt(m[1], 10) <= maxStudent;
  });
}

function readRestoredBulkUpload() {
  const draft = readParentsBulkUploadDraft();
  if (!draft) {
    return {
      file: null as File | null,
      step: 'select' as BulkUploadStep,
      csvData: [] as Array<ParentRegistrationData>,
      headers: [] as Array<string>,
      columnMappingTableData:
        [] as Array<ParentRegistrationColumnMappingTableData>,
    };
  }

  return {
    file: fileFromDraft(draft, unparse(draft.csvData)),
    step: draft.step,
    csvData: draft.csvData,
    headers: draft.headers,
    columnMappingTableData: draft.columnMappingTableData,
  };
}

export function BulkUploadParents() {
  const navigate = useNavigate();
  const mutation = useBulkUploadParentsMutation();
  const [restored] = useState(readRestoredBulkUpload);

  const [file, setFile] = useState<File | null>(restored.file);

  const [step, setStep] = useState<BulkUploadStep>(restored.step);
  const [csvData, setCSVData] = useState<Array<ParentRegistrationData>>(
    restored.csvData,
  );
  const [headers, setHeaders] = useState<Array<string>>(restored.headers);
  const [columnMappingTableData, setColumnMappingTableData] = useState<
    Array<ParentRegistrationColumnMappingTableData>
  >(restored.columnMappingTableData);

  const [bulkImportJobId, setBulkImportJobId] = useState<string | null>(null);
  const [bulkImportImmediateResult, setBulkImportImmediateResult] =
    useState<ParentsBulkImportJobResult | null>(null);
  const [bulkImportModalOpen, setBulkImportModalOpen] = useState(false);
  const [bulkImportInitialData, setBulkImportInitialData] = useState<{
    totalRecords: number;
    estimatedTime?: string;
    correlationId?: string;
    deduplicated?: boolean;
  } | null>(null);

  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  useEffect(() => {
    if (!file || csvData.length === 0) return;
    writeParentsBulkUploadDraft({
      fileName: file.name,
      step,
      csvData,
      headers,
      columnMappingTableData,
    });
  }, [file, step, csvData, headers, columnMappingTableData]);

  const { data: bulkImportJobStatus } = useGetParentsBulkImportStatus({
    jobId: bulkImportJobId ?? '',
  });

  const job = bulkImportJobStatus?.job;
  const jobStatus = job?.status;
  const isBulkImportProcessing =
    jobStatus === 'queued' || jobStatus === 'processing';

  useEffect(() => {
    if (!isBulkImportProcessing) {
      return;
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isBulkImportProcessing]);

  const handleUpload = useCallback(async () => {
    if (!file || !csvData.length) return;

    setBulkImportJobId(null);
    setBulkImportImmediateResult(null);
    setBulkImportInitialData({
      totalRecords: countParentGroupsFromCsvRows(csvData),
    });
    setBulkImportModalOpen(true);
    setUploadProgress(0);
    await Promise.resolve();

    try {
      const csvString = unparse(csvData);
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const fileToUpload = new File([blob], file.name || 'parents.csv', {
        type: 'text/csv',
      });
      const uploadResponse = await mutation.mutateAsync({
        file: fileToUpload,
        onUploadProgress: (pct) => {
          setUploadProgress(pct);
        },
      });
      if (isParentsBulkUploadQueuedResponse(uploadResponse)) {
        if (uploadResponse.deduplicated) {
          toast.message(
            'An import for this file is already running — showing that job.',
          );
        }
        setBulkImportImmediateResult(null);
        setBulkImportJobId(uploadResponse.jobId);
        setBulkImportInitialData({
          totalRecords: uploadResponse.totalRecords,
          estimatedTime: uploadResponse.estimatedTime,
          correlationId: uploadResponse.correlationId,
          deduplicated: uploadResponse.deduplicated,
        });
        setUploadProgress(100);
        clearParentsBulkUploadDraft();
      } else {
        setBulkImportJobId(null);
        setBulkImportImmediateResult({
          success: uploadResponse.success,
          failed: uploadResponse.failed,
          errors: uploadResponse.errors ?? [],
          batchId: uploadResponse.batchId,
          duplicateUnresolved: [],
        });
        setBulkImportInitialData({
          totalRecords: uploadResponse.success + uploadResponse.failed,
        });
        clearParentsBulkUploadDraft();
      }
    } catch (error) {
      setBulkImportModalOpen(false);
      let detail = 'Could not start import. Check your file and try again.';
      if (isAxiosError(error)) {
        const raw = error.response?.data?.message;
        const fromServer =
          typeof raw === 'string'
            ? raw
            : Array.isArray(raw)
              ? raw.join(' ')
              : null;
        if (fromServer?.trim()) {
          detail = fromServer.trim();
        }
      }
      toast.error(detail);
      setUploadProgress(null);
    } finally {
      setFile(null);
    }
  }, [file, csvData, mutation]);

  const onFileChange = useCallback((file: File | null) => {
    if (!file) return;
    setFile(file);
    parse<ParentRegistrationData>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const parsedCSVData = result.data;
        const parsedHeaders = result.meta.fields ?? [];

        const getColumnValue = (header?: ParentRegistrationCSVHeader) => {
          const csvRow = parsedCSVData[0];
          if (!header || !csvRow) return '';
          return csvRow[header as keyof ParentRegistrationData];
        };

        const maxStudent = getMaxStudentFromHeaders(parsedHeaders);
        const tableRows = TABLE_DATA.map((row) => {
          const columnHeader = parsedHeaders.find(
            (header) => header === row.header,
          );
          return {
            ...row,
            selectedColumn: columnHeader ?? '',
            columnValue: getColumnValue(
              columnHeader as ParentRegistrationCSVHeader,
            ),
          };
        });
        const filteredRows = filterRowsByStudentCount(tableRows, maxStudent);

        setCSVData(parsedCSVData);
        setHeaders(parsedHeaders);
        setColumnMappingTableData(filteredRows);
      },
    });
  }, []);

  const onNext = useCallback(() => {
    if (!file) return;
    setStep('mapping');
  }, [file]);

  return (
    <div className='flex min-h-screen flex-col'>
      <div className='w-full p-2 pl-1'>
        <div className='mb-8 flex items-center justify-between'>
          <Link to='/dashboard' className='flex items-center gap-2'>
            <CircleArrowLeft className='size-6' />
            <span className='text-lg font-semibold'>Back to Dashboard</span>
          </Link>
          <div className='flex gap-2'>
            <Button variant={'outline'} asChild>
              <Link to='/parents/bulk-upload/history'>Import history</Link>
            </Button>
            <Button variant={'outline'} asChild>
              <Link to='/parents/csv-batch-processing'>
                Parents Onboarding Email Batch
              </Link>
            </Button>
          </div>
        </div>

        {step === 'select' && (
          <FileSelector file={file} setFile={onFileChange} onNext={onNext} />
        )}
        {step === 'mapping' && (
          <ColumnMapping
            columnHeaders={headers}
            csvData={csvData}
            setCSVData={setCSVData}
            setStep={setStep}
            columnMappingTableData={columnMappingTableData}
            setColumnMappingTableData={setColumnMappingTableData}
          />
        )}
        {step === 'validate' && (
          <ValidateCSV
            columnHeaders={headers}
            csvData={csvData}
            setStep={setStep}
            handleUpload={handleUpload}
            isUploading={mutation.isPending}
            uploadProgress={uploadProgress}
          />
        )}
      </div>

      <ParentsBulkImportProgressModal
        jobId={bulkImportJobId}
        immediateResult={bulkImportImmediateResult}
        open={bulkImportModalOpen}
        isRequestInFlight={mutation.isPending}
        uploadProgress={uploadProgress}
        initialData={bulkImportInitialData ?? undefined}
        onOpenChange={(open) => {
          setBulkImportModalOpen(open);
          if (!open) {
            setBulkImportJobId(null);
            setBulkImportImmediateResult(null);
            setBulkImportInitialData(null);
          }
        }}
        onComplete={() => {
          void navigate({ to: '/students' });
        }}
      />
    </div>
  );
}
